import type { Au08BenchmarkDataset } from "./benchmark-types.js";

// AU-08 "How you compare" benchmark dataset — APRA March 2026 quarter.
//
// Figures are the number of people, by age cohort, with private
// HOSPITAL-treatment cover, transcribed VERBATIM from APRA's Quarterly Private
// Health Insurance Statistics, "Membership and Coverage" workbook, Table T5
// ("Coverage of Hospital Treatment Tables Offered by Health Benefits Funds —
// Insured Persons by Gender and Age Cohort"), the Mar-2026 Total column. The
// per-band figures sum to APRA's published total of 12,790,111.
//
// Versioned by the period it covers (quarter ending 31 March 2026). NEVER edit
// a shipped dataset to refresh figures — author a new benchmarks-YYYY-qN.ts for
// the next APRA quarter and register it alongside this one (immutability rule,
// same as the rulesets).
//
// The coverage RATE is computed (insuredPersons ÷ population), never stored —
// see coverageRate() in benchmark-types. The denominator is the ABS Estimated
// Resident Population by age. APRA publishes no by-age denominator (its coverage
// % is state-level only), so we pair the APRA Mar-2026 insured counts with the
// latest available ABS ERP-by-age (Sep-2025 quarter — ABS publishes ERP-by-age
// ~2 quarters in arrears; the Mar-2026 ERP isn't released until 17 Sep 2026).
// Both periods are cited on the result page; nothing is modelled or
// interpolated — both figures are real published quarters.
//
// `population` is the ABS ERP for the band, aggregated from single-year-of-age
// (so the 85-89 / 90-94 / 95+ split matches APRA — ABS pre-aggregates only
// "85 and over"). Regenerate for a new quarter with `tools/refresh-au08-benchmark.ts`.
export const benchmarks2026Q1: Au08BenchmarkDataset = {
  id: "apra-2026-q1",
  period: "APRA March 2026 quarter",
  populationPeriod: "ABS ERP, September 2025 quarter",
  measure: "people with private hospital cover, by age (APRA)",
  sources: {
    apraStatistics: "https://www.apra.gov.au/quarterly-private-health-insurance-statistics",
    apraMembershipCoverageWorkbook:
      "https://www.apra.gov.au/sites/default/files/2026-05/Quarterly%20Private%20Health%20Insurance%20Membership%20Coverage%20March%202026.xlsx",
    absErpRelease:
      "https://www.abs.gov.au/statistics/people/population/national-state-and-territory-population/latest-release",
    absErpDataApi: "https://data.api.abs.gov.au/rest/data/ABS,ERP_Q,1.0.0/1.3..AUS.Q",
  },
  sourcesVerifiedAt: "2026-06-13",
  // insuredPersons: APRA Table T5, Mar-2026 Total column (numerator).
  // population: ABS ERP_Q, AUS / Persons / 2025-Q3, single years aggregated to
  // these bands (denominator). Both transcribed verbatim from their source.
  byAgeBand: {
    "0-4": { insuredPersons: 553_752, population: 1_518_410 },
    "5-9": { insuredPersons: 724_221, population: 1_601_600 },
    "10-14": { insuredPersons: 810_131, population: 1_685_963 },
    "15-19": { insuredPersons: 802_340, population: 1_717_126 },
    "20-24": { insuredPersons: 607_394, population: 1_829_739 },
    "25-29": { insuredPersons: 563_115, population: 2_035_414 },
    "30-34": { insuredPersons: 771_252, population: 2_071_534 },
    "35-39": { insuredPersons: 933_534, population: 2_026_277 },
    "40-44": { insuredPersons: 988_984, population: 1_924_603 },
    "45-49": { insuredPersons: 883_455, population: 1_662_034 },
    "50-54": { insuredPersons: 868_235, population: 1_680_088 },
    "55-59": { insuredPersons: 823_553, population: 1_565_655 },
    "60-64": { insuredPersons: 790_018, population: 1_533_452 },
    "65-69": { insuredPersons: 750_551, population: 1_395_871 },
    "70-74": { insuredPersons: 662_727, population: 1_197_731 },
    "75-79": { insuredPersons: 575_768, population: 1_019_220 },
    "80-84": { insuredPersons: 369_562, population: 653_322 },
    "85-89": { insuredPersons: 203_961, population: 381_949 },
    "90-94": { insuredPersons: 82_999, population: 165_913 },
    "95+": { insuredPersons: 24_559, population: 58_843 },
  },
};

/** Total hospital-treatment insured persons APRA published for this period.
 * The per-band `insuredPersons` sum to this value (a dataset invariant). */
export const APRA_2026_Q1_TOTAL_HOSPITAL_INSURED = 12_790_111;

/** Total ABS ERP (all ages, AUS, Persons) for the denominator period. The
 * per-band `population` figures sum to this value (a dataset invariant). */
export const ABS_ERP_2025_Q3_TOTAL = 27_724_744;
